import { defineComponent as _defineComponent } from 'vue';
import { computed, getCurrentInstance, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router/composables';
import { useActions, useGetters } from 'vuex-composition-helpers';
import { PrismEditor } from 'vue-prism-editor';
import 'vue-prism-editor/dist/prismeditor.min.css';
// @ts-ignore
import { highlight, languages } from 'prismjs/components/prism-core';
import 'prismjs/components/prism-sql';
import 'prismjs/themes/prism-tomorrow.css';
import { useClipboard } from '@/utils/use-clipboard';
import draggable from "vuedraggable";
import AIFailureHelp from './Query/AIFailureHelp.vue';
import QueryField from './Query/QueryField.vue';
import QueryTemplateSelectModal from './Query/QueryTemplateSelectModal.vue';
export default /*#__PURE__*/ _defineComponent({
    __name: 'QueryManagement',
    setup(__props) {
        const instance = getCurrentInstance()?.proxy;
        const { get: fetchFeedInfo } = useActions('feed', ['get']);
        const { create: createNewQuery, patch: patchQuery, find: fetchQueries } = useActions('query', ['create', 'patch', 'find']);
        const { patch: patchFeed } = useActions('feed', ['patch']);
        const { get: getFeedFromStore } = useGetters('feed', ['get']);
        const { find: findQueriesInStore, get: getQuery } = useGetters('query', ['find', 'get']);
        const aiPrompt = ref('');
        const isQuery = ref(true);
        const waitingAIResponse = ref(false);
        // Chat message history
        const messages = ref([]);
        const feedId = useRoute().params.id;
        const feed = computed(() => {
            if (!feedId)
                return null;
            return getFeedFromStore.value(feedId);
        });
        const params = computed(() => {
            return {
                query: {
                    feedId,
                    $sort: { priority: 1, createdAt: -1 },
                }
            };
        });
        const queries = computed({
            set(value) {
                value.forEach((item, index) => {
                    item.priority = index;
                    patchQuery([item._id, item, {}]);
                });
            },
            get() {
                const item = findQueriesInStore.value(params.value);
                return item.data;
            }
        });
        function newQuery() {
            const data = {
                name: 'Your Query',
                code: `SELECT * FROM ${feed.value?.tableName ?? 'table'};`,
                select_code: `SELECT * FROM ${feed.value?.tableName ?? 'table'};`,
                feedId,
                priority: queries.value.length,
                useAI: "never",
            };
            createNewQuery([data])
                .then(() => {
                instance?.$toasted.global.success({
                    message: 'Query Added',
                });
            })
                .catch((errors) => {
                instance?.$toasted.global.error({
                    message: errors.message,
                });
            });
        }
        onMounted(() => {
            fetchFeedInfo(feedId);
            fetchQueries(params.value);
        });
        const chainedMode = computed({
            get() {
                return feed.value?.isQueryExecutionChained ?? false;
            },
            set(value) {
                patchFeed([feedId, { isQueryExecutionChained: value }, {}]);
            },
        });
        const progress = ref(0);
        const { create: startProccess } = useActions('queue-management', ['create']);
        const router = useRouter();
        async function processXML() {
            isQuery.value = true;
            runFailCause.value = null;
            progress.value = 0;
            try {
                const task = await startProccess({
                    feedId,
                    task: 'full-workflow',
                });
                router.push({ name: 'feed.runDetails', params: { runId: task._id } });
            }
            catch (error) {
                instance?.$toasted.global.error(error);
                runFailCause.value = error.message;
                console.error(error);
            }
            finally {
                isQuery.value = false;
                progress.value = -1;
            }
        }
        const runFailCause = ref(null);
        const { copyIntoClipboard } = useClipboard();
        const queryTemplateSelectModalOpen = ref(false);
        function applyTemplate(template) {
            console.log('applyTemplate', template);
            Promise.all(template.map(query => {
                const data = {
                    name: 'Your Query',
                    code: 'SELECT 2 as example',
                    select_code: 'SELECT 2 as example',
                    feedId,
                    useAI: "never",
                    ...query,
                    priority: queries.value.length,
                };
                return createNewQuery(data);
            })).then(() => {
                instance?.$toasted.global.success({
                    message: 'Applied template'
                });
            });
        }
        const { create: promptAI } = useActions('ai-query-helper', ['create']);
        // AI chat functions
        function highlighter(code) {
            return highlight(code, languages.sql);
        }
        function hasCodeBlock(content) {
            return content.includes('```');
        }
        function extractCode(content) {
            const codeMatch = content.match(/```(?:sql)?([\s\S]*?)```/);
            return codeMatch ? codeMatch[1].trim() : '';
        }
        function formatMessage(content) {
            // Replace code blocks with placeholders for display
            return content.replace(/```(?:sql)?([\s\S]*?)```/g, '<div class="code-placeholder">[Code block]</div>');
        }
        function copyMessage(content) {
            copyIntoClipboard(content);
            instance?.$toasted.global.success({
                message: 'Copied to clipboard'
            });
        }
        function clearMessages() {
            messages.value = [];
        }
        async function runAI() {
            if (!aiPrompt.value.trim())
                return;
            // Add user message to chat
            messages.value.push({
                content: aiPrompt.value,
                isUser: true,
                timestamp: new Date()
            });
            waitingAIResponse.value = true;
            try {
                const response = await promptAI({
                    feedId: feedId,
                    message: aiPrompt.value,
                });
                // Add AI response to chat
                messages.value.push({
                    content: response.message,
                    isUser: false,
                    timestamp: new Date()
                });
                // Clear input after sending
                aiPrompt.value = '';
                // Scroll to bottom after message is added
                scrollToBottom();
            }
            catch (error) {
                instance?.$toasted.global.error({
                    message: error.message || 'Failed to get AI response'
                });
            }
            finally {
                waitingAIResponse.value = false;
            }
        }
        // Add new function to scroll to bottom of chat
        const chatMessagesContainer = ref(null);
        function scrollToBottom() {
            setTimeout(() => {
                if (chatMessagesContainer.value) {
                    chatMessagesContainer.value.scrollTop = chatMessagesContainer.value.scrollHeight;
                }
            }, 100);
        }
        // Add watch to scroll to bottom when messages change
        watch(messages, () => {
            scrollToBottom();
        }, { deep: true });
        return { __sfc: true, instance, fetchFeedInfo, createNewQuery, patchQuery, fetchQueries, patchFeed, getFeedFromStore, findQueriesInStore, getQuery, aiPrompt, isQuery, waitingAIResponse, messages, feedId, feed, params, queries, newQuery, chainedMode, progress, startProccess, router, processXML, runFailCause, copyIntoClipboard, queryTemplateSelectModalOpen, applyTemplate, promptAI, highlighter, hasCodeBlock, extractCode, formatMessage, copyMessage, clearMessages, runAI, chatMessagesContainer, scrollToBottom, PrismEditor, draggable, AIFailureHelp, QueryField, QueryTemplateSelectModal };
    }
});
